/**
 * Real-Time Data Service
 * 
 * Fetches live data from Agrotrack API which connects to:
 * - OpenWeatherMap for weather data
 * - USDA QuickStats for crop statistics
 * - Sentinel Hub / NASA for satellite imagery
 * - Integrated Pest Management models
 * - Biodiversity aggregated sources
 */

import { weatherService } from './WeatherService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

class RealTimeDataService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.defaultLocation = { lat: 38.9072, lon: -77.0369 }; // DMV region
  }

  /**
   * Make API request with caching and error handling
   */
  async fetchData(endpoint, params = {}, options = {}) {
    const url = new URL(`${this.baseURL}${endpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });

    const cacheKey = url.toString();

    // Check cache unless explicitly disabled
    if (!options.noCache && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
      this.cache.delete(cacheKey);
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        signal: AbortSignal.timeout(options.timeout || 30000)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Cache successful response
      if (!options.noCache) {
        this.cache.set(cacheKey, { data, timestamp: Date.now() });
      }

      return data;
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    }
  }

  // ==================== API STATUS ====================

  /**
   * Check API health and data source configuration
   */
  async getAPIStatus() {
    try {
      return await this.fetchData('/api/status', {}, { noCache: true });
    } catch (error) {
      return {
        status: 'offline',
        error: error.message,
        instructions: 'Start the API server: cd api && uvicorn main:app --reload'
      };
    }
  }

  /**
   * Check if ML models are loaded
   */
  async getHealthStatus() {
    try {
      return await this.fetchData('/health', {}, { noCache: true });
    } catch (error) {
      return { status: 'offline', models_loaded: false };
    }
  }

  // ==================== WEATHER DATA ====================

  /**
   * Get current weather and forecast
   */
  async getWeather(lat = null, lon = null) {
    // Redirect to client-side WeatherService (Open-Meteo)
    // to ensure live data without requiring a backend server
    try {
      const data = await weatherService.getCurrentWeather(
        lat || this.defaultLocation.lat,
        lon || this.defaultLocation.lon
      );

      // Adapter: Convert WeatherService format to expected API format
      return {
        current: {
          temperature: data.temperature,
          humidity: data.humidity,
          wind_speed: data.windSpeed,
          wind_direction: data.windDirection,
          weather_code: data.condition // Approximated
        },
        daily: [
          {
            precipitation_amount: data.daily.precipitation,
            temperature_2m_max: data.daily.maxTemp,
            temperature_2m_min: data.daily.minTemp
          }
        ]
      };
    } catch (error) {
      console.warn("WeatherService failed, falling back to API logic", error);
      const location = this._getLocation(lat, lon);
      return this.fetchData('/api/weather/current', location);
    }
  }

  /**
   * Get agricultural-specific weather metrics
   */
  async getAgriculturalWeather(lat = null, lon = null) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/weather/agricultural', location);
  }

  /**
   * Get historical weather data
   */
  async getHistoricalWeather(lat = null, lon = null, days = 30) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/weather/historical', { ...location, days });
  }

  // ==================== CROP DATA ====================

  /**
   * Get crop statistics
   */
  async getCropStats(state = 'VIRGINIA', year = null, crop = null) {
    const currentYear = year || new Date().getFullYear();
    return this.fetchData('/api/crops/stats', { state, year: currentYear, crop });
  }

  /**
   * Get crop distribution for a region
   */
  async getCropDistribution(state = 'VIRGINIA', year = null) {
    const currentYear = year || new Date().getFullYear();
    return this.fetchData('/api/crops/distribution', { state, year: currentYear });
  }

  /**
   * Get crop progress (planting, emergence, harvest)
   */
  async getCropProgress(state = 'VIRGINIA', crop = 'CORN') {
    return this.fetchData('/api/crops/progress', { state, crop });
  }

  /**
   * Get crop health/condition data
   */
  async getCropHealth(state = 'VIRGINIA', crop = 'CORN') {
    return this.fetchData('/api/crops/health', { state, crop });
  }

  /**
   * Get historical yield data
   */
  async getCropYields(state = 'VIRGINIA', crop = 'CORN', years = 5) {
    return this.fetchData('/api/crops/yields', { state, crop, years });
  }

  // ==================== SATELLITE / NDVI DATA ====================

  /**
   * Get comprehensive NDVI data for a region
   */
  async getNDVIData(lat = null, lon = null, radiusKm = 50) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/satellite/ndvi', { ...location, radius_km: radiusKm });
  }

  /**
   * Get NDVI for a specific point
   */
  async getPointNDVI(lat, lon) {
    return this.fetchData('/api/satellite/ndvi/point', { lat, lon });
  }

  /**
   * Get NDVI time series
   */
  async getNDVITimeSeries(lat = null, lon = null, days = 90) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/satellite/ndvi/timeseries', { ...location, days });
  }

  /**
   * Get recent satellite imagery metadata
   */
  async getRecentImagery(lat = null, lon = null, days = 30) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/satellite/imagery', { ...location, days });
  }

  /**
   * Get vegetation change detection
   */
  async getVegetationChanges(lat = null, lon = null, threshold = 0.1) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/satellite/change-detection', { ...location, threshold });
  }

  // ==================== BIODIVERSITY DATA ====================

  /**
   * Get biodiversity metrics
   */
  async getBiodiversityMetrics(lat = null, lon = null, radiusKm = 25) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/biodiversity/metrics', { ...location, radius_km: radiusKm });
  }

  /**
   * Get species list
   */
  async getSpeciesList(lat = null, lon = null, category = null) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/biodiversity/species', { ...location, category });
  }

  /**
   * Get pollinator-specific data
   */
  async getPollinatorData(lat = null, lon = null) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/biodiversity/pollinators', location);
  }

  /**
   * Get biodiversity trends over time
   */
  async getBiodiversityTrends(lat = null, lon = null, years = 5) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/biodiversity/trends', { ...location, years });
  }

  // ==================== PEST PREDICTION DATA ====================

  /**
   * Get pest and disease predictions
   */
  async getPestPredictions(lat = null, lon = null, crop = 'corn') {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/pest/predict', { ...location, crop });
  }

  /**
   * Get historical pest data
   */
  async getPestHistory(lat = null, lon = null, pest = null, years = 3) {
    const location = this._getLocation(lat, lon);
    return this.fetchData('/api/pest/history', { ...location, pest, years });
  }

  /**
   * Get IPM recommendations
   */
  async getIPMRecommendations(crop = 'corn', growthStage = 'vegetative') {
    return this.fetchData('/api/pest/recommendations', { crop, growth_stage: growthStage });
  }

  // ==================== ML PREDICTIONS ====================

  /**
   * Get yield prediction from ML models
   */
  async getYieldPrediction(features, modelName = null) {
    try {
      const response = await fetch(`${this.baseURL}/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          features,
          model_name: modelName
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Prediction error:', error);
      throw error;
    }
  }

  /**
   * Get list of available ML models
   */
  async getModels() {
    return this.fetchData('/models');
  }

  // ==================== AGGREGATED DATA ====================

  /**
   * Get all dashboard data in a single call
   * Fetches weather, crops, NDVI, biodiversity, and pest data
   */
  async getDashboardData(lat = null, lon = null, state = 'VIRGINIA') {
    const location = this._getLocation(lat, lon);

    try {
      const [weather, crops, ndvi, biodiversity, pest] = await Promise.allSettled([
        this.getWeather(location.lat, location.lon),
        this.getCropDistribution(state),
        this.getNDVIData(location.lat, location.lon),
        this.getBiodiversityMetrics(location.lat, location.lon),
        this.getPestPredictions(location.lat, location.lon)
      ]);

      return {
        weather: weather.status === 'fulfilled' ? weather.value : null,
        crops: crops.status === 'fulfilled' ? crops.value : null,
        ndvi: ndvi.status === 'fulfilled' ? ndvi.value : null,
        biodiversity: biodiversity.status === 'fulfilled' ? biodiversity.value : null,
        pest: pest.status === 'fulfilled' ? pest.value : null,
        timestamp: new Date().toISOString(),
        errors: {
          weather: weather.status === 'rejected' ? weather.reason?.message : null,
          crops: crops.status === 'rejected' ? crops.reason?.message : null,
          ndvi: ndvi.status === 'rejected' ? ndvi.reason?.message : null,
          biodiversity: biodiversity.status === 'rejected' ? biodiversity.reason?.message : null,
          pest: pest.status === 'rejected' ? pest.reason?.message : null
        }
      };
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Get location with fallback to default
   */
  _getLocation(lat, lon) {
    return {
      lat: lat ?? this.defaultLocation.lat,
      lon: lon ?? this.defaultLocation.lon
    };
  }

  /**
   * Set default location for all requests
   */
  setDefaultLocation(lat, lon) {
    this.defaultLocation = { lat, lon };
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Set cache timeout
   */
  setCacheTimeout(ms) {
    this.cacheTimeout = ms;
  }
}

// Export singleton instance
const realTimeDataService = new RealTimeDataService();
export default realTimeDataService;

// Also export class for testing
export { RealTimeDataService };
